import { useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { 
  Mail, MapPin, Globe, CheckCircle, Send, Shield
} from "lucide-react";
import { SEO } from "@/components/shared";
import { EmailVerification } from "@/components/EmailVerification";
import { MEDIA, fadeInUp, staggerContainer, HERO_IMAGES } from "@/constants";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICE_KEYS = ['gtm', 'digital', 'sales', 'ai', 'intel', 'other'];
const PREFILL_MESSAGES = {
  'ai-coe': "We would like to receive a proposal for setting up an Intel Unnati AI Centre of Excellence at our institution. Please share the details and next steps.",
};
const EMPTY_FORM = { name: '', email: '', phone: '', company: '', message: '', service: '' };

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const source = PREFILL_MESSAGES[searchParams.get('ref')] ? searchParams.get('ref') : '';
  const prefillService = SERVICE_KEYS.includes(searchParams.get('service')) ? searchParams.get('service') : '';
  const [formData, setFormData] = useState(() => ({
    ...EMPTY_FORM,
    service: prefillService,
    message: PREFILL_MESSAGES[source] || '',
  }));
  const [pending, setPending] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaError, setCaptchaError] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0, token: '' });

  // Fetch server-signed captcha on mount
  const fetchCaptcha = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/captcha`);
      if (res.ok) {
        const data = await res.json();
        setCaptcha(data);
      }
    } catch {
      // Fallback: generate client-side (less secure but functional)
      const n1 = Math.floor(Math.random() * 10) + 2;
      const n2 = Math.floor(Math.random() * 10) + 2;
      setCaptcha({ num1: n1, num2: n2, token: '' });
    }
  }, []);

  useEffect(() => {
    fetchCaptcha();
  }, [fetchCaptcha]);

  useEffect(() => {
    if (!source) return undefined;
    const t = setTimeout(() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' }), 300);
    return () => clearTimeout(t);
  }, [source]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (honeypot) return;
    
    const userAnswer = parseInt(captchaAnswer);
    if (isNaN(userAnswer) || userAnswer !== captcha.num1 + captcha.num2) {
      setCaptchaError(true);
      return;
    }
    
    setCaptchaError(false);
    setSubmitError('');
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          honeypot,
          captcha_token: captcha.token,
          captcha_answer: userAnswer,
          source,
        }),
      });
      
      if (response.status === 429) {
        setSubmitError('Too many submissions. Please try again in an hour.');
        return;
      }
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setSubmitted(false);
        setPending({ id: data.submission_id, email: formData.email });
        setCaptchaAnswer('');
        fetchCaptcha();
      } else {
        setSubmitError(data.detail || 'Failed to send message. Please try again.');
        fetchCaptcha();
      }
    } catch {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  }, [formData, honeypot, captchaAnswer, captcha, fetchCaptcha, source]);

  const handleVerified = useCallback(() => {
    setPending(null);
    setSubmitted(true);
    setFormData({ ...EMPTY_FORM, service: prefillService, message: PREFILL_MESSAGES[source] || '' });
  }, [prefillService, source]);

  const handleResetVerification = useCallback(() => setPending(null), []);

  return (
    <div className="pt-24">
      <SEO 
        title="Contact Us"
        description="Get in touch with Qbic Catalyst Partners. Contact us for consulting services, training programs, and partnership inquiries. Email: info@qbiccatalyst.com"
        keywords="contact Qbic Catalyst, consulting inquiry, training programs contact, business consultation, partnership inquiry"
        path="/contact"
      />
      {/* Hero with Image */}
      <section className="section page-hero" style={{ "--hero-img": `url(${HERO_IMAGES.contact})` }}>
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="initial"
              animate="animate"
              variants={staggerContainer}
            >
              <motion.p variants={fadeInUp} className="text-[#C9A962] text-sm uppercase tracking-[0.2em] mb-4">
                Contact Us
              </motion.p>
              <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl font-bold mb-6">
                Let's Start a{' '}
                <span className="text-gradient">Conversation</span>
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-xl text-[#A0ABC0]">
                Ready to transform your organization? Get in touch with our team to discuss your needs.
              </motion.p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex justify-center"
            >
              <img 
                src={MEDIA.contactPage} 
                alt="Contact Us"
                className="rounded-lg shadow-2xl w-full max-w-md object-cover aspect-video"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section id="contact-form" className="section theme-light tint-ivory scroll-mt-16">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-[#0A1628] rounded-lg p-8 border border-[#2A3648]"
            >
              {pending ? (
                <EmailVerification
                  submissionId={pending.id}
                  email={pending.email}
                  onVerified={handleVerified}
                  onReset={handleResetVerification}
                />
              ) : (
              <>
              <h3 className="text-2xl font-bold mb-6">{source ? 'Request a Proposal' : 'Send Us a Message'}</h3>
              
              {submitted && (
                <div className="mb-6 p-4 bg-[#C9A962]/10 border border-[#C9A962]/30 rounded-lg text-[#C9A962]" data-testid="contact-submitted-message">
                  <CheckCircle className="inline mr-2" size={18} />
                  Submitted! Your email is verified and your message has reached our team. We'll get back to you shortly.
                </div>
              )}
              
              {submitError && (
                <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400" data-testid="contact-error">
                  {submitError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <input
                  type="text"
                  name="website"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  style={{ position: 'absolute', left: '-9999px' }}
                  tabIndex={-1}
                  autoComplete="off"
                />
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label>Full Name <span className="text-[#C9A962]">*</span></label>
                    <input
                      type="text"
                      data-testid="contact-name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Your name"
                      required
                    />
                  </div>
                  <div>
                    <label>Email Address <span className="text-[#C9A962]">*</span></label>
                    <input
                      type="email"
                      data-testid="contact-email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="you@company.com"
                      required
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label>Phone Number <span className="text-[#C9A962]">*</span></label>
                    <input
                      type="tel"
                      data-testid="contact-phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+91 98765 43210"
                      required
                    />
                  </div>
                  <div>
                    <label>Company <span className="text-[#C9A962]">*</span></label>
                    <input
                      type="text"
                      data-testid="contact-company"
                      value={formData.company}
                      onChange={(e) => setFormData({...formData, company: e.target.value})}
                      placeholder="Your organization"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label>Service of Interest <span className="text-[#C9A962]">*</span></label>
                  <select
                    data-testid="contact-service"
                    value={formData.service}
                    onChange={(e) => setFormData({...formData, service: e.target.value})}
                    required
                  >
                    <option value="">Select a service</option>
                    <option value="gtm">Go-To-Market Consulting</option>
                    <option value="digital">Digital Transformation</option>
                    <option value="sales">Sales Transformation Programs</option>
                    <option value="ai">AI Catalyst Training</option>
                    <option value="intel">Intel Partnership Programs</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label>Message <span className="text-[#C9A962]">*</span></label>
                  <textarea
                    data-testid="contact-message"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    placeholder="Tell us about your requirements..."
                    rows={5}
                    required
                  />
                </div>
                
                <div className="p-4 bg-[#121E30] rounded-lg border border-[#2A3648]">
                  <label className="flex items-center gap-2 mb-2">
                    <Shield className="text-[#C9A962]" size={16} />
                    <span>Security Verification <span className="text-[#C9A962]">*</span></span>
                  </label>
                  <p className="text-[#A0ABC0] text-sm mb-3">
                    Please solve: What is {captcha.num1} + {captcha.num2}?
                  </p>
                  <input
                    type="number"
                    data-testid="contact-captcha"
                    value={captchaAnswer}
                    onChange={(e) => {
                      setCaptchaAnswer(e.target.value);
                      setCaptchaError(false);
                    }}
                    placeholder="Enter your answer"
                    required
                    className="max-w-[200px]"
                  />
                  {captchaError && (
                    <p className="text-red-400 text-sm mt-2">Incorrect answer. Please try again.</p>
                  )}
                </div>
                
                <button 
                  type="submit" 
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" 
                  data-testid="contact-submit"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      Continue to Email Verification <Send size={18} />
                    </>
                  )}
                </button>
              </form>
              </>
              )}
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <h3 className="text-2xl font-bold mb-6">Get in Touch</h3>
              <p className="text-[#A0ABC0] mb-8">
                We're here to help you navigate your transformation journey. Reach out to us through any of the following channels.
              </p>

              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <div className="icon-container flex-shrink-0">
                    <Mail className="text-[#C9A962]" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Email</p>
                    <a href="mailto:info@qbiccatalyst.com" className="text-[#A0ABC0] hover:text-[#C9A962]">
                      info@qbiccatalyst.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="icon-container flex-shrink-0">
                    <Globe className="text-[#C9A962]" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Website</p>
                    <a href="https://www.qbic-catalyst.com" target="_blank" rel="noopener noreferrer" className="text-[#A0ABC0] hover:text-[#C9A962]">
                      www.qbic-catalyst.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="icon-container flex-shrink-0">
                    <MapPin className="text-[#C9A962]" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Global Presence</p>
                    <p className="text-[#A0ABC0]">
                      Serving clients worldwide with a focus on transformative impact
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-[#0A1628] rounded-lg p-8 border border-[#2A3648]">
                <h4 className="font-bold mb-4">Subscribe to Our Newsletter</h4>
                <p className="text-[#A0ABC0] text-sm mb-4">
                  Stay updated with the latest insights, programs, and industry trends.
                </p>
                <form className="flex gap-3">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="flex-1"
                    data-testid="newsletter-email"
                  />
                  <button type="submit" className="btn-primary px-6" data-testid="newsletter-submit">
                    Subscribe
                  </button>
                </form>
              </div>

              <div className="mt-8 flex items-center gap-4 p-4 bg-[#0A1628] rounded-lg border border-[#2A3648]">
                <Shield className="text-[#C9A962]" size={32} />
                <div>
                  <p className="font-semibold">ISO/IEC 27001:2022 Certified</p>
                  <p className="text-[#A0ABC0] text-sm">Your data security is our priority</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
